<!--
  Angular 15: *ngIf / *ngFor only, no @if blocks.
-->
<div class="dl-page">

  <div class="dl-page__header">
    <div class="dl-page__heading">
      <h1 class="dl-page__title">Legal documents</h1>
      <p class="dl-page__subtitle">
        Terms of Use, Privacy Policy and Data Processing Agreement for each app. The products read the
        General part followed by their own addendum. Publishing a new version makes every user of that
        app accept it again before they can continue - the DPA, every organisation's owner.
      </p>
    </div>
    <div class="dl-page__actions">
      <button type="button" class="dl-btn" *ngIf="!loading && composed" (click)="togglePreview()" [disabled]="previewing"
              data-testid="legal-preview">
        <mat-icon>{{ preview ? 'visibility_off' : 'visibility' }}</mat-icon>
        {{ preview ? 'Hide preview' : 'Preview as users read it' }}
      </button>
      <button type="button" class="dl-btn dl-btn--primary" (click)="newVersion()" [disabled]="loading || starting"
              data-testid="legal-new-version">
        <mat-icon>{{ draftOf(selectedType) ? 'edit' : 'add' }}</mat-icon>
        {{ draftOf(selectedType) ? 'Continue draft' : (currentOf(selectedType) ? 'New version' : 'Write first version') }}
      </button>
    </div>
  </div>

  <div class="dl-loading" *ngIf="loading">
    <div class="dl-spinner"></div>
    <p>Loading…</p>
  </div>

  <div class="dl-banner dl-banner--error" *ngIf="error">
    <mat-icon>error</mat-icon>
    <div class="dl-banner__body">{{ error }}</div>
    <div class="dl-banner__actions">
      <button type="button" class="dl-btn dl-btn--sm" (click)="load()">Try again</button>
    </div>
  </div>

  <div class="legal-platforms" role="tablist" aria-label="Platform" *ngIf="!loading" data-testid="legal-platforms">
    <button type="button" *ngFor="let p of platforms"
            class="legal-platform"
            role="tab"
            [class.legal-platform--active]="p === platform"
            [attr.aria-selected]="p === platform"
            (click)="selectPlatform(p)"
            [attr.data-testid]="'legal-platform-' + p">
      <mat-icon>{{ platformIcons[p] }}</mat-icon>
      {{ platformLabels[p] }}
      <span class="legal-platform__count">{{ countOf(p) }}</span>
    </button>
  </div>

  <div class="dl-card legal-card legal-preview" *ngIf="preview" data-testid="legal-preview-card">
    <div class="dl-card__body">
      <p class="legal-summary">
        {{ platformLabels[preview.platform] }} {{ typeLabels[preview.type] }} as its users will read it: each
        part's draft where there is one, its current version otherwise.
      </p>
      <section *ngFor="let part of preview.parts" class="legal-preview__part"
               [attr.data-testid]="'legal-preview-part-' + part.platform">
        <h3>{{ part.title }} <small>{{ platformLabels[part.platform] }} · {{ part.version ? 'v' + part.version : 'draft' }}</small></h3>
        <div class="legal-markdown" [innerHTML]="part.content | markdown"></div>
      </section>
      <p *ngIf="preview.parts.length === 0" class="legal-summary">Nothing written yet.</p>
    </div>
  </div>

  <mat-tab-group *ngIf="!loading" animationDuration="0ms" [selectedIndex]="selectedIndex"
                 (selectedIndexChange)="onTabChange($event)" data-testid="legal-tabs">
    <mat-tab *ngFor="let type of types" [label]="typeLabels[type]">

      <app-empty-state icon="gavel" [title]="'No ' + platformLabels[platform] + ' ' + typeLabels[type] + ' yet'"
                       *ngIf="documentsOf(type).length === 0">
        Write the first version. Nobody is asked to accept anything until it is published.
      </app-empty-state>

      <div class="dl-card legal-card" *ngIf="documentsOf(type).length > 0" [class.dl-is-refreshing]="refreshing">
        <table class="dl-table dl-table--cards" dlCardTable [attr.data-testid]="'legal-table-' + type">
          <thead>
            <tr>
              <th class="dl-table__numeric">Version</th>
              <th>Title</th>
              <th>Status</th>
              <th>Published</th>
              <th class="dl-table__numeric">{{ organisational ? 'Organisations accepted' : 'Accepted' }}</th>
              <th class="dl-table__numeric">Not yet</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let doc of documentsOf(type); trackBy: trackById"
                class="dl-row--clickable"
                (click)="open(doc)"
                [attr.data-testid]="'legal-row-' + doc.id"
                [attr.data-status]="doc.status">
              <td class="dl-table__numeric">{{ doc.version ? 'v' + doc.version : '—' }}</td>
              <td class="dl-cell--title">
                {{ doc.title }}
                <div class="legal-summary" *ngIf="doc.changeSummary">{{ doc.changeSummary }}</div>
              </td>
              <td><span [ngClass]="statusClass(doc)">{{ statusLabel(doc) }}</span></td>
              <td>{{ doc.publishedAt ? (doc.publishedAt | date:'dd.MM.yy HH:mm') : '—' }}</td>
              <td class="dl-table__numeric">{{ doc.status === 'PUBLISHED' ? doc.acceptedCount : '—' }}</td>
              <td class="dl-table__numeric">{{ doc.pendingCount ?? '—' }}</td>
              <td class="dl-cell--bare">
                <button type="button" class="dl-btn dl-btn--ghost dl-btn--icon" *ngIf="doc.status === 'DRAFT'"
                        (click)="openEdit(doc, $event)" matTooltip="Edit draft" aria-label="Edit draft"
                        data-testid="legal-edit">
                  <mat-icon>edit</mat-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>

<app-legal-document-form-dialog
  [isOpen]="formOpen"
  [document]="editing"
  [type]="selectedType"
  [platform]="platform"
  (saved)="onSaved($event)"
  (cancel)="formOpen = false">
</app-legal-document-form-dialog>
